import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Building2, Trash2, RotateCw, PauseCircle, PlayCircle } from "lucide-react";
import { useReadOnly } from "../hooks/useReadOnly";
import { useAuth } from "../contexts/AuthContext";
import { SubscriptionNotice } from "../components/SubscriptionBanner";
import { MODULES, LEVEL_OPTIONS, defaultPermissionsForRole } from "../lib/permissions";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const ROLE_LABEL = {
  superadmin: "Superadministrador",
  owner: "Administrador empresa",
  contador: "Contador",
  usuario: "Usuario",
  demo: "Demostración",
  admin: "Administrador",
};

const OWNER_INVITE_ROLES = [
  { value: "contador", label: "Contador" },
  { value: "usuario", label: "Usuario" },
];

const SUPER_INVITE_ROLES = [
  { value: "owner", label: "Administrador empresa" },
  { value: "contador", label: "Contador" },
  { value: "usuario", label: "Usuario" },
];

function PermissionsGrid({ permisos, onChange, disabled }) {
  return (
    <div className="border border-[#E2DFD8] rounded-md divide-y max-h-64 overflow-y-auto">
      {MODULES.map((m) => (
        <div key={m.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
          <div>
            <div className="font-medium text-[#1C211F]">{m.label}</div>
            <div className="text-[10px] text-[#A3ABA6] uppercase tracking-wide">{m.group}</div>
          </div>
          <select
            disabled={disabled}
            value={permisos[m.id] || "none"}
            onChange={(e) => onChange({ ...permisos, [m.id]: e.target.value })}
            className="text-xs border border-[#E2DFD8] rounded-sm px-2 py-1 bg-white"
          >
            {LEVEL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}

export default function Usuarios() {
  const readOnly = useReadOnly();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin" && !user?.organizacion_id;
  const canInvite = !readOnly && (isSuperadmin || user?.role === "owner");

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "contador",
    permisos: defaultPermissionsForRole("contador"),
  });

  const [orgOpen, setOrgOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({
    nombre: "",
    plan: "estandar",
    max_usuarios: 25,
    max_empresas: 10,
    dias_vigencia: 365,
    empresa_nombre: "",
    empresa_rut: "",
    empresa_giro: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewDias, setRenewDias] = useState(30);

  const [empOpen, setEmpOpen] = useState(false);
  const [empForm, setEmpForm] = useState({
    nombre: "",
    rut: "",
    giro: "",
  });
  const [orgToDelete, setOrgToDelete] = useState(null);

  const loadOrgs = async () => {
    const { data } = await api.get("/organizaciones");
    setOrgs(data);
    if (data.length && !selectedOrg) setSelectedOrg(data[0].id);
  };

  const loadUsers = async () => {
    try {
      const params = isSuperadmin && selectedOrg ? { params: { organizacion_id: selectedOrg } } : {};
      const { data } = await api.get("/usuarios", params);
      setUsers(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (isSuperadmin) {
        try {
          await loadOrgs();
        } catch (e) {
          toast.error(extractError(e));
        }
      }
      await loadUsers();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, isSuperadmin]);

  const onRoleChange = (role) => {
    setUserForm((f) => ({
      ...f,
      role,
      permisos: defaultPermissionsForRole(role),
    }));
  };

  const submitUser = async (e) => {
    e.preventDefault();
    const orgId = isSuperadmin ? selectedOrg : undefined;
    if (isSuperadmin && !orgId) {
      toast.error("Seleccione una organización");
      return;
    }
    try {
      const q = new URLSearchParams({ role: userForm.role });
      if (orgId) q.set("organizacion_id", orgId);
      await api.post(`/usuarios?${q}`, {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        permisos: { modulos: userForm.permisos },
      });
      toast.success("Usuario creado");
      setUserOpen(false);
      setUserForm({
        name: "",
        email: "",
        password: "",
        role: "contador",
        permisos: defaultPermissionsForRole("contador"),
      });
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const submitOrg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/organizaciones/provision", orgForm);
      toast.success("Organización creada con empresa y administrador");
      setOrgOpen(false);
      setOrgForm({
        nombre: "",
        plan: "estandar",
        max_usuarios: 25,
        max_empresas: 10,
        dias_vigencia: 365,
        empresa_nombre: "",
        empresa_rut: "",
        empresa_giro: "",
        owner_name: "",
        owner_email: "",
        owner_password: "",
      });
      await loadOrgs();
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const confirmDeleteOrg = async () => {
    if (!orgToDelete) return;
    try {
      await api.delete(`/organizaciones/${orgToDelete.id}`);
      toast.success(`Organización «${orgToDelete.nombre}» eliminada`);
      if (selectedOrg === orgToDelete.id) setSelectedOrg("");
      setOrgToDelete(null);
      await loadOrgs();
      loadUsers();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const submitEmpresa = async (e) => {
    e.preventDefault();
    if (!selectedOrg) {
      toast.error("Seleccione una organización");
      return;
    }
    try {
      await api.post("/organizaciones/empresas", {
        organizacion_id: selectedOrg,
        ...empForm,
      });
      toast.success("Empresa (RUT) agregada");
      setEmpOpen(false);
      setEmpForm({ nombre: "", rut: "", giro: "" });
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const selectedOrgData = orgs.find((o) => o.id === selectedOrg);
  const selectedOrgLabel = selectedOrgData?.nombre;

  const orgEstadoLabel = (o) => {
    if (o.sin_limite) return "Sin límite";
    if (o.expirada) return "Expirada";
    if (!o.activa) return "Suspendida";
    return "Activa";
  };

  const submitRenew = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      const { data } = await api.post(`/organizaciones/${selectedOrg}/renovar`, {
        dias: renewDias,
      });
      toast.success(
        `Vigencia extendida hasta ${fmtDate(data.vigente_hasta)} (${data.dias_restantes ?? renewDias} días restantes)`
      );
      setRenewOpen(false);
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const toggleOrgActiva = async (org, activa) => {
    try {
      await api.patch(`/organizaciones/${org.id}/estado`, { activa });
      toast.success(activa ? "Organización reactivada" : "Organización suspendida");
      await loadOrgs();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <Page>
      {!isSuperadmin && user?.organizacion && (
        <div className="mb-4 rounded-md overflow-hidden border border-[#E2DFD8]">
          <SubscriptionNotice org={user.organizacion} />
        </div>
      )}
      <PageHeader
        title={isSuperadmin ? "Organizaciones y usuarios" : "Usuarios"}
        subtitle={
          readOnly
            ? "Vista de demostración"
            : isSuperadmin
              ? "Cree clientes (organizaciones), RUT y equipo con permisos por módulo"
              : "Equipo de su organización"
        }
        testid="usuarios-header"
      />

      {isSuperadmin && (
        <section className="mb-8 bg-white border border-[#E2DFD8] rounded-md p-6" data-testid="orgs-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-lg font-medium">Organizaciones cliente</h2>
            <div className="flex gap-2">
              <Dialog open={orgOpen} onOpenChange={setOrgOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    data-testid="new-org-button"
                    className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-3 py-2 rounded-sm"
                  >
                    <Plus className="h-4 w-4" /> Nueva organización
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Alta de organización</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitOrg} className="space-y-3">
                    <p className="text-xs text-[#59645E]">
                      Crea la cuenta cliente, su primer RUT y el usuario administrador (owner).
                    </p>
                    <input
                      required
                      placeholder="Nombre organización (ej. Amelu Craft)"
                      value={orgForm.nombre}
                      onChange={(e) => setOrgForm({ ...orgForm, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        min={1}
                        placeholder="Máx. usuarios"
                        value={orgForm.max_usuarios}
                        onChange={(e) => setOrgForm({ ...orgForm, max_usuarios: +e.target.value })}
                        className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                      />
                      <input
                        type="number"
                        min={1}
                        placeholder="Máx. empresas/RUT"
                        value={orgForm.max_empresas}
                        onChange={(e) => setOrgForm({ ...orgForm, max_empresas: +e.target.value })}
                        className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                      />
                    </div>
                    <input
                      type="number"
                      min={1}
                      required
                      placeholder="Días de vigencia (suscripción)"
                      value={orgForm.dias_vigencia}
                      onChange={(e) => setOrgForm({ ...orgForm, dias_vigencia: +e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <p className="text-xs text-[#59645E]">
                      Tras esos días los usuarios de la organización no podrán acceder hasta renovar.
                    </p>
                    <div className="label-caps pt-2">Primera empresa (RUT)</div>
                    <input
                      required
                      placeholder="Razón social"
                      value={orgForm.empresa_nombre}
                      onChange={(e) => setOrgForm({ ...orgForm, empresa_nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <input
                      required
                      placeholder="RUT"
                      value={orgForm.empresa_rut}
                      onChange={(e) => setOrgForm({ ...orgForm, empresa_rut: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <div className="label-caps pt-2">Administrador (owner)</div>
                    <input
                      required
                      placeholder="Nombre"
                      value={orgForm.owner_name}
                      onChange={(e) => setOrgForm({ ...orgForm, owner_name: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <input
                      required
                      type="email"
                      placeholder="Correo login"
                      value={orgForm.owner_email}
                      onChange={(e) => setOrgForm({ ...orgForm, owner_email: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <input
                      required
                      type="password"
                      minLength={6}
                      placeholder="Contraseña"
                      value={orgForm.owner_password}
                      onChange={(e) => setOrgForm({ ...orgForm, owner_password: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                    />
                    <DialogFooter>
                      <button type="submit" className="bg-[#1B4332] text-white text-sm px-4 py-2 rounded-sm">
                        Crear organización
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <table className="acc-table mb-4" data-testid="orgs-table">
            <thead>
              <tr>
                <th>Organización</th>
                <th>Plan</th>
                <th>Usuarios</th>
                <th>Empresas</th>
                <th>Vigencia</th>
                <th>Estado</th>
                <th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr
                  key={o.id}
                  className={selectedOrg === o.id ? "bg-[#1B4332]/5" : ""}
                  onClick={() => setSelectedOrg(o.id)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="font-medium">{o.nombre}</td>
                  <td>{o.plan}</td>
                  <td>
                    {o.n_usuarios} / {o.max_usuarios}
                  </td>
                  <td>
                    {o.n_empresas} / {o.max_empresas}
                  </td>
                  <td className="text-sm">
                    {o.sin_limite ? (
                      "—"
                    ) : (
                      <>
                        {fmtDate(o.vigente_hasta)}
                        {o.dias_restantes != null && !o.expirada && (
                          <span className="block text-[10px] text-[#A3ABA6]">
                            {o.dias_restantes} días restantes
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        o.expirada || !o.activa
                          ? "text-red-700 text-xs font-medium"
                          : "text-[#1B4332] text-xs font-medium"
                      }
                    >
                      {orgEstadoLabel(o)}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      title="Eliminar organización"
                      className="p-1.5 text-red-700 hover:bg-red-50 rounded-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOrgToDelete(o);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {selectedOrg && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#59645E]">
              <span>
                Seleccionada: <strong className="text-[#1C211F]">{selectedOrgLabel}</strong>
                {selectedOrgData && !selectedOrgData.sin_limite && (
                  <span className="ml-2 text-xs">
                    — vigente hasta {fmtDate(selectedOrgData.vigente_hasta)}
                    {selectedOrgData.expirada ? " (expirada)" : ""}
                  </span>
                )}
              </span>
              <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border border-[#E2DFD8] px-2 py-1 rounded-sm hover:bg-[#F9F8F6]"
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Renovar vigencia
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Renovar {selectedOrgLabel}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitRenew} className="space-y-3">
                    <p className="text-xs text-[#59645E]">
                      Se suman días desde hoy o desde la fecha de expiración actual si aún no venció.
                    </p>
                    <input
                      type="number"
                      min={1}
                      required
                      value={renewDias}
                      onChange={(e) => setRenewDias(+e.target.value)}
                      className="w-full px-3 py-2 border rounded-sm text-sm"
                      placeholder="Días a agregar"
                    />
                    <DialogFooter>
                      <button type="submit" className="bg-[#1B4332] text-white text-sm px-4 py-2 rounded-sm">
                        Renovar
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              {selectedOrgData && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 border border-[#E2DFD8] px-2 py-1 rounded-sm hover:bg-[#F9F8F6]"
                  onClick={() => toggleOrgActiva(selectedOrgData, !selectedOrgData.activa)}
                >
                  {selectedOrgData.activa ? (
                    <>
                      <PauseCircle className="h-3.5 w-3.5" /> Suspender
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-3.5 w-3.5" /> Reactivar
                    </>
                  )}
                </button>
              )}
              <Dialog open={empOpen} onOpenChange={setEmpOpen}>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border border-[#E2DFD8] px-2 py-1 rounded-sm hover:bg-[#F9F8F6]"
                  >
                    <Building2 className="h-3.5 w-3.5" /> Agregar RUT
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Otra empresa en {selectedOrgLabel}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitEmpresa} className="space-y-3">
                    <input
                      required
                      placeholder="Razón social"
                      value={empForm.nombre}
                      onChange={(e) => setEmpForm({ ...empForm, nombre: e.target.value })}
                      className="w-full px-3 py-2 border rounded-sm text-sm"
                    />
                    <input
                      required
                      placeholder="RUT"
                      value={empForm.rut}
                      onChange={(e) => setEmpForm({ ...empForm, rut: e.target.value })}
                      className="w-full px-3 py-2 border rounded-sm text-sm"
                    />
                    <DialogFooter>
                      <button type="submit" className="bg-[#1B4332] text-white text-sm px-4 py-2 rounded-sm">
                        Agregar
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <AlertDialog open={!!orgToDelete} onOpenChange={(open) => !open && setOrgToDelete(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar organización?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se borrará permanentemente «{orgToDelete?.nombre}», todos sus usuarios, empresas
                  (RUT) y datos contables. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-700 hover:bg-red-800"
                  onClick={confirmDeleteOrg}
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}

      <section className="bg-white border border-[#E2DFD8] rounded-md overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-[#E2DFD8]">
          <h2 className="font-display text-lg font-medium">
            {isSuperadmin ? `Usuarios — ${selectedOrgLabel || "seleccione org"}` : "Equipo"}
          </h2>
          {canInvite && (
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="new-user-button"
                  disabled={isSuperadmin && !selectedOrg}
                  className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-50 text-white text-sm font-medium px-3 py-2 rounded-sm"
                >
                  <Plus className="h-4 w-4" /> Nuevo usuario
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Alta de usuario</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitUser} className="space-y-3" data-testid="new-user-form">
                  {isSuperadmin && (
                    <p className="text-xs text-[#59645E]">
                      Organización: <strong>{selectedOrgLabel}</strong>
                    </p>
                  )}
                  <input
                    required
                    placeholder="Nombre"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                  <input
                    required
                    type="email"
                    placeholder="Correo"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                  <input
                    required
                    type="password"
                    minLength={6}
                    placeholder="Contraseña"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm"
                  />
                  <Select value={userForm.role} onValueChange={onRoleChange}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Rol" />
                    </SelectTrigger>
                    <SelectContent>
                      {(isSuperadmin ? SUPER_INVITE_ROLES : OWNER_INVITE_ROLES).map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div>
                    <div className="label-caps mb-2">Permisos por módulo</div>
                    <PermissionsGrid
                      permisos={userForm.permisos}
                      onChange={(permisos) => setUserForm({ ...userForm, permisos })}
                    />
                  </div>
                  <DialogFooter>
                    <button type="submit" className="bg-[#1B4332] text-white text-sm px-4 py-2 rounded-sm">
                      Crear usuario
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
        ) : (
          <table className="acc-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Creado</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-[#59645E] py-8 text-sm">
                    {isSuperadmin && !selectedOrg
                      ? "Seleccione una organización"
                      : "Sin usuarios en esta organización"}
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.name}</td>
                    <td className="font-mono text-[12px]">{u.email}</td>
                    <td>
                      <span className="inline-flex px-2 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-[#1B4332]/10 text-[#1B4332]">
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td>{fmtDate(u.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </section>
    </Page>
  );
}
